// Rules of the left column's tree (R-U7, R-U24, um/2026-10-30/03 п.1, п.6): which folders are open and which item is lit.

import { menuGroups, menuViews, type MenuItems, type MenuSection } from '@confdeck/core-shared';

/**
 * What the address points at, to light up its item and open the folders on the way to it. `extra` — an item an app adds under a section of records (the page of a pipeline): it is the one lit, not its section.
 */
export interface NavActive { typeId?: string; viewId?: string | null; screen?: string; extra?: string }

/** The node lists the item the address points at. */
export const navLists = (n: MenuItems, a: NavActive) => (!!a.typeId && n.typeIds.includes(a.typeId)) || (!!a.screen && !!n.screens?.some((x) => x.key === a.screen)) || (!!a.viewId && !!n.views?.some((v) => v.viewId === a.viewId));

/** The key under which the open state of a section of records (its views and what an app adds under it) is kept. */
export const typeKey = (typeId: string) => `type:${typeId}`;

/** Views the menu shows as items of their own: a section of records does not list them again under itself. */
export const menuViewIds = (section: MenuItems): Set<string> => new Set(menuViews(section).map((v) => v.viewId));

/**
 * Folders and sections of records on the way to the open item. They open once, on the first visit, and that is remembered;
 * after that only the person opens and closes them (um/2026-10-30/03 п.6: opening «Компании» does not fold «Сделки»).
 */
export function pathKeys(section: MenuSection, active: NavActive, expandable: (typeId: string) => boolean): string[] {
  const keys = menuGroups(section).filter((x) => navLists(x.group, active) || menuGroups(x.group).some((y) => navLists(y.group, active))).map((x) => x.group.key);
  // A view that is an item of its own is not «under» its type: the type does not open for it.
  if (active.typeId && !(active.viewId && menuViewIds(section).has(active.viewId)) && expandable(active.typeId)) keys.push(typeKey(active.typeId));
  return keys;
}

/**
 * The views the view button offers (R-U24). A view the menu shows as an item of its own («Партнёры» — companies of the chosen event)
 * belongs to that item: from its list the button offers only it, and the other lists of the type do not offer it — picking it in
 * «Компании» of CRM would take the column to «Билеты» (MiniMax review 31.10, P2-3).
 */
export function switchViews<V extends { id: string }>(views: V[], itemViews: Set<string>, current: string | undefined): V[] {
  if (current && itemViews.has(current)) return views.filter((v) => v.id === current);
  return views.filter((v) => !itemViews.has(v.id));
}

/** The keys to remember as open now: on the way to the item and never chosen by the person before. */
export const keysToOpen = (saved: Record<string, boolean>, path: string[]): string[] => path.filter((k) => saved[k] === undefined);

/**
 * What a visit adds to the saved folders: the way to the open item, open, for the folders the person never chose. The left column
 * stores the result; empty — nothing to store.
 */
export const openOnVisit = (saved: Record<string, boolean>, section: MenuSection, active: NavActive, expandable: (typeId: string) => boolean): Record<string, boolean> =>
  Object.fromEntries(keysToOpen(saved, pathKeys(section, active, expandable)).map((k) => [k, true]));

/** A folder is open by the person's choice; without one — by the manifest (`collapsed` closes it). */
export const isOpen = (saved: Record<string, boolean>, key: string, closedByDefault = false): boolean => saved[key] ?? !closedByDefault;

/**
 * Exactly one item is lit (um/2026-10-30/03, coordinator): a view that is a menu item lights only itself, not the item of its type
 * («Билеты» does not light «Участники», a view of tickets shown as its own item).
 */
export function litItem(section: MenuItems, active: NavActive): { kind: 'view' | 'type' | 'screen' | 'extra' | 'none'; id?: string } {
  if (active.extra) return { kind: 'extra', id: active.extra };
  if (active.screen) return { kind: 'screen', id: active.screen };
  if (active.viewId && menuViewIds(section).has(active.viewId)) return { kind: 'view', id: active.viewId };
  if (active.typeId) return { kind: 'type', id: active.typeId };
  return { kind: 'none' };
}

